/**
 * Central source of truth for AI/search engine entity data.
 *
 * This file feeds JSON-LD structured data (schema.org) and /llms.txt.
 * Keeping product + organization facts here means AI engines (ChatGPT,
 * Claude, Perplexity, Gemini, Google AI Overviews) read ONE consistent set
 * of names, descriptions, and URLs across the whole site.
 *
 * ⚠️ EDIT THE VALUES MARKED `TODO` to match your real Prismic page slugs,
 * social profiles, and contact details, then redeploy.
 */

const SITE_URL = "https://grubpos.com"

const ORGANIZATION = {
  name: "Grub24",
  legalName: "Grub24", // TODO: registered company name if different
  url: SITE_URL,
  logo: `${SITE_URL}/logo.png`,
  description:
    "Grub24 is a restaurant technology platform providing EPOS, kitchen display systems, self-ordering kiosks, digital ordering, and restaurant websites for independent restaurants, cafés, and takeaways.",
  // sameAs links tell AI engines this is the SAME entity across the web.
  // TODO: replace with your real, live profile URLs (remove any you don't have).
  sameAs: [
    "https://twitter.com/grub24uk",
    "https://www.facebook.com/grub24uk",
    "https://www.instagram.com/grubpos",
    "https://www.linkedin.com/company/grub24",
    "https://www.youtube.com/@grub2483",
  ],
  // One ContactPoint per market. areaServed uses ISO 3166 codes (UAE = AE).
  contactPoints: [
    {
      contactType: "sales",
      telephone: "+44-20-3754-7282",
      email: "info@grubpos.co.uk",
      areaServed: "GB",
      availableLanguage: ["English"],
    },
    {
      contactType: "sales",
      telephone: "+971-50-328-8018",
      email: "info@grubpos.co.uk",
      areaServed: "AE",
      availableLanguage: ["English", "Arabic"],
    },
  ],
}

/**
 * The product range. Each entry becomes a schema.org SoftwareApplication
 * (or Product) so AI engines can answer "what does Grub24 offer?" and
 * recommend the right product for a user's need.
 *
 * `url` must point to the real, live page for that product.
 * TODO: confirm each `url` matches your actual Prismic page slug.
 */
/**
 * Published prices per market. Feeds the EPOS Offer schema and llms.txt so
 * AI engines quote the right currency for each market. Keep in sync with the
 * pricing sections in Prismic (en-gb, en-ae, ar-ae).
 */
const PRICING = [
  { plan: "Single System", market: "GB", price: 12, currency: "GBP", per: "week" },
  { plan: "Double System", market: "GB", price: 18, currency: "GBP", per: "week" },
  { plan: "Single System", market: "AE", price: 179, currency: "AED", per: "month" },
  { plan: "Double System", market: "AE", price: 349, currency: "AED", per: "month" },
]

/** UAE delivery platforms with a live integration. */
const UAE_DELIVERY_INTEGRATIONS = ["Noon", "Deliveroo"]

const PRODUCTS = [
  {
    slug: "epos",
    offers: true, // attach PRICING as schema.org Offers
    name: "Grub24 EPOS",
    shortName: "EPOS System",
    type: "SoftwareApplication",
    applicationCategory: "BusinessApplication",
    url: `${SITE_URL}/epos/`,
    image: `${SITE_URL}/logo.png`,
    description:
      "Grub24 EPOS is an electronic point-of-sale system for restaurants, cafés, and takeaways. It handles in-store and table ordering, payments, menu management, and real-time sales reporting, syncing with the rest of the Grub24 platform.",
    keywords: ["EPOS", "point of sale", "POS system", "restaurant till"],
  },
  {
    slug: "kds",
    name: "Grub24 Kitchen Display System",
    shortName: "KDS",
    type: "SoftwareApplication",
    applicationCategory: "BusinessApplication",
    url: `${SITE_URL}/kitchen-display-system/`,
    image: `${SITE_URL}/logo.png`,
    description:
      "Grub24 KDS (Kitchen Display System) replaces paper tickets with digital screens in the kitchen. Orders from EPOS, kiosks, and online ordering route straight to the right station, improving prep speed, accuracy, and order timing.",
    keywords: ["KDS", "kitchen display system", "kitchen screen", "order management"],
  },
  {
    slug: "kiosk",
    name: "Grub24 Self-Ordering Kiosk",
    shortName: "Self-Ordering Kiosk",
    type: "SoftwareApplication",
    applicationCategory: "BusinessApplication",
    url: `${SITE_URL}/kiosks/`,
    image: `${SITE_URL}/logo.png`,
    description:
      "Grub24 Self-Ordering Kiosks let customers browse the menu, customise items, and pay on a touchscreen in-store. Orders flow directly to the kitchen via KDS, reducing queues, increasing average order value, and freeing up staff.",
    keywords: ["self ordering kiosk", "self service kiosk", "touchscreen ordering"],
  },
  {
    slug: "digital-ordering",
    name: "Grub24 Digital Ordering",
    shortName: "Digital Ordering",
    type: "SoftwareApplication",
    applicationCategory: "BusinessApplication",
    url: `${SITE_URL}/mobile-order-and-pay/`, // closest live page; no /digital-ordering page exists
    image: `${SITE_URL}/logo.png`,
    description:
      "Grub24 Digital Ordering gives restaurants their own online and mobile ordering channel for delivery, collection, and dine-in. Commission-free ordering syncs with EPOS and KDS so every order lands in one system.",
    keywords: ["online ordering", "digital ordering", "mobile ordering", "commission-free ordering"],
  },
  {
    slug: "website",
    name: "Grub24 Restaurant Websites",
    shortName: "Restaurant Website",
    type: "SoftwareApplication",
    applicationCategory: "BusinessApplication",
    url: `${SITE_URL}/branded-website/`,
    image: `${SITE_URL}/logo.png`,
    description:
      "Grub24 builds branded restaurant websites with integrated online ordering. Each site is fast, mobile-friendly, SEO-ready, and connected to the Grub24 platform so orders go straight to the kitchen.",
    keywords: ["restaurant website", "restaurant web design", "ordering website"],
  },
]

module.exports = {
  SITE_URL,
  ORGANIZATION,
  PRODUCTS,
  PRICING,
  UAE_DELIVERY_INTEGRATIONS,
}
